import {
  BodyScrollEndEvent,
  ColDef,
  FirstDataRenderedEvent,
  RowClassParams,
  RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { RefObject, useCallback, useMemo, useRef, useState } from "react";

import { aggridHeaderClass } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import GridTouch from "src/components/common/gridWrapper/Grid_Touch";
import {
  defaultColDef,
  getDefaultRowIndexColumnConfig,
  restoreGridScrollState,
  saveGridScrollState,
} from "src/utils/grid/gridStyleUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";

import {
  defaultKSK2130_R01DetailDto,
  KSK2130_R01DetailDto,
} from "./Dto_KSK2130_R01";
import { useKSK2130_R01Store } from "./Store_KSK2130_R01";

const COLUMN_STATE_KEY = "KSK2130_R01-detail-grid-column-state-v2";
const HEADER_NAMES: Record<string, string> = {
  rowIndex: "",
  warehouseNm: "출고창고",
  itemCd: "자재코드",
  itemNm: "품명",
  itemSpec: "규격",
  lotNo: "Lot No",
  lotQty: "자재투입량",
  stockQty: "LOT재고량",
};

interface DetailTableKSK2130_R01Props {
  gridRef: RefObject<AgGridReact<KSK2130_R01DetailDto> | null>;
}

export const DetailTable_KSK2130_R01 = ({
  gridRef,
}: DetailTableKSK2130_R01Props) => {
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const {
    KSK2130_R01DetailList,
    selectedKSK2130_R01DetailList,
    addSelectedKSK2130_R01DetailList,
    removeSelectedKSK2130_R01DetailList,
    initSelectedKSK2130_R01DetailList,
    deletedKSK2130_R01DetailList,
    detailColumnState,
    setDetailColumnState,
    detailScrollTop,
    setDetailScrollTop,
  } = useKSK2130_R01Store();
  const { getRowId, paddedGridData } =
    useGridSettingContent<KSK2130_R01DetailDto>({
      gridRef,
      key: "valueIndex",
      gridData: KSK2130_R01DetailList,
      defaultGridData: defaultKSK2130_R01DetailDto,
      selectedRow: selectedKSK2130_R01DetailList,
    });
  const getCellStyle = useCallback(
    (params: any, key: string) =>
      onlyTouchGridStyle(key, "warehouseNm")(params),
    [],
  );

  const baseColumnDefs = useMemo<ColDef<KSK2130_R01DetailDto>[]>(
    () => [
      ...getDefaultRowIndexColumnConfig(),
      {
        headerName: "출고창고",
        colId: "warehouseNm",
        field: "warehouseNm",
        width: 130,
        cellStyle: (params) => getCellStyle(params, "warehouseNm"),
      },
      { headerName: "자재코드", colId: "itemCd", field: "itemCd", width: 150,
        cellStyle: (params) => getCellStyle(params, "itemCd") },
      { headerName: "품명", colId: "itemNm", field: "itemNm", width: 180,
        cellStyle: (params) => getCellStyle(params, "itemNm") },
      { headerName: "규격", colId: "itemSpec", field: "itemSpec", width: 150,
        cellStyle: (params) => getCellStyle(params, "itemSpec") },
      { headerName: "Lot No", colId: "lotNo", field: "lotNo", width: 160,
        cellStyle: (params) => getCellStyle(params, "lotNo") },
      {
        headerName: "자재투입량",
        colId: "lotQty",
        field: "lotQty",
        width: 100,
        type: "numericColumn",
        cellStyle: (params) => getCellStyle(params, "lotQty"),
        valueFormatter: (params) =>
          Number(params.value ?? 0).toLocaleString(),
      },
      {
        headerName: "LOT재고량",
        colId: "stockQty",
        field: "stockQty",
        width: 100,
        type: "numericColumn",
        cellStyle: (params) => getCellStyle(params, "stockQty"),
        valueFormatter: (params) =>
          Number(params.value ?? 0).toLocaleString(),
      },
    ],
    [getCellStyle],
  );

  const columnDefs = useMemo<ColDef<KSK2130_R01DetailDto>[]>(() =>
    GridCustomColumnDefs<KSK2130_R01DetailDto>({
      baseColumnDefs,
      columnState: detailColumnState?.some((state) => state.colId === "rowIndex")
        ? detailColumnState
        : null,
      headerNames: HEADER_NAMES,
      fallbackHeaderClass: aggridHeaderClass,
    }) as ColDef<KSK2130_R01DetailDto>[], [baseColumnDefs, detailColumnState]);

  const pinnedBottomRowData = useMemo<KSK2130_R01DetailDto[]>(
    () => [
      {
        ...defaultKSK2130_R01DetailDto,
        warehouseNm: `${KSK2130_R01DetailList.length.toLocaleString()}건`,
        lotQty: KSK2130_R01DetailList.reduce(
          (sum, item) => sum + Number(item.lotQty || 0),
          0,
        ),
        stockQty: KSK2130_R01DetailList.reduce(
          (sum, item) => sum + Number(item.stockQty || 0),
          0,
        ),
      },
    ],
    [KSK2130_R01DetailList],
  );

  const rowClassRules = useMemo<
    Record<
      string,
      (params: RowClassParams<KSK2130_R01DetailDto>) => boolean
    >
  >(
    () => ({
      selected: (params) =>
        Boolean(
          !params.node.rowPinned &&
            params.data &&
            selectedKSK2130_R01DetailList.includes(
              params.data.valueIndex.toString(),
            ),
        ),
      deleted: (params) =>
        Boolean(
          !params.node.rowPinned &&
            params.data &&
            deletedKSK2130_R01DetailList.includes(
              params.data.valueIndex.toString(),
            ),
        ),
      inserted: (params) => Boolean(!params.node.rowPinned && params.data?.inserted),
      hover: (params) => params.node.rowIndex === hoveredRowIndex,
    }),
    [
      deletedKSK2130_R01DetailList,
      hoveredRowIndex,
      selectedKSK2130_R01DetailList,
    ],
  );

  const handleRowClick = useCallback(
    (event: RowClickedEvent<KSK2130_R01DetailDto>) => {
      if (event.node.rowPinned || !event.data) return;

      const valueIndex = String(event.data.valueIndex);
      if (deletedKSK2130_R01DetailList.includes(valueIndex)) return;

      if (selectedKSK2130_R01DetailList.includes(valueIndex)) {
        removeSelectedKSK2130_R01DetailList([valueIndex]);
      } else {
        addSelectedKSK2130_R01DetailList([valueIndex]);
      }
    },
    [
      addSelectedKSK2130_R01DetailList,
      deletedKSK2130_R01DetailList,
      removeSelectedKSK2130_R01DetailList,
      selectedKSK2130_R01DetailList,
    ],
  );

  const handleBodyScrollEnd = useCallback((
    event: BodyScrollEndEvent<KSK2130_R01DetailDto>,
  ) => {
    if (event.direction === "vertical") {
      saveGridScrollState(event.api, setDetailScrollTop);
    }
  }, [setDetailScrollTop]);

  const handleFirstDataRendered = useCallback((
    event: FirstDataRenderedEvent<KSK2130_R01DetailDto>,
  ) => {
    restoreGridScrollState(event.api, detailScrollTop);
  }, [detailScrollTop]);

  return (
    <GridContextWrapper<KSK2130_R01DetailDto>
      gridRef={gridRef}
      gridWrapperRef={gridWrapperRef}
      paddedGridData={paddedGridData}
      initSelected={initSelectedKSK2130_R01DetailList}
      defaultRowData={defaultKSK2130_R01DetailDto}
      baseColumnDefs={columnDefs}
      setColumnState={setDetailColumnState}
      localStorageKey={COLUMN_STATE_KEY}
      enableDataHistory={false}
    >
      <GridTouch<KSK2130_R01DetailDto>
        ref={gridRef}
        rowData={paddedGridData}
        getRowId={getRowId}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowClassRules={rowClassRules}
        onRowClicked={handleRowClick}
        onBodyScrollEnd={handleBodyScrollEnd}
        onFirstDataRendered={handleFirstDataRendered}
        pinnedBottomRowData={pinnedBottomRowData}
        setHoveredRowIndex={setHoveredRowIndex}
        columnStateKey={COLUMN_STATE_KEY}
        setColumnState={setDetailColumnState}
      />
    </GridContextWrapper>
  );
};
